import { useState, useCallback, useEffect } from 'react'
import type { SortRule } from './sortRows'

export type { SortRule }

export interface GridState {
  sortRules: SortRule[]
  freezeCount: number
  colWidths: Record<string, number>
  colOrder: string[]
  toggleSort: (col: string, multi: boolean) => void
  setColWidth: (col: string, width: number) => void
  setColOrder: (order: string[]) => void
  setFreezeCount: (n: number) => void
}

// Hash format: #sort=col:dir,col:dir&freeze=2
// No route-key prefix — production uses React Router path routing, hash is pure state.

function readHashState(): { sort: SortRule[]; freeze: number } {
  const raw = window.location.hash.replace('#', '')
  const params = new URLSearchParams(raw)

  const sortStr = params.get('sort') ?? ''
  const sort: SortRule[] = sortStr
    ? sortStr.split(',').flatMap(s => {
        const [col, dir] = s.split(':')
        return col && (dir === 'asc' || dir === 'desc') ? [{ col, dir }] : []
      })
    : []

  const freeze = Math.max(0, parseInt(params.get('freeze') ?? '0', 10) || 0)
  return { sort, freeze }
}

function writeHashState(sort: SortRule[], freeze: number) {
  const params = new URLSearchParams()
  if (sort.length > 0) params.set('sort', sort.map(r => `${r.col}:${r.dir}`).join(','))
  if (freeze > 0) params.set('freeze', String(freeze))
  const stateStr = params.toString()
  history.replaceState(null, '', stateStr ? `#${stateStr}` : window.location.pathname)
}

const lsKey = (gridId: string) => `instagrid:layout:${gridId}`

function readLayout(gridId: string): { colWidths: Record<string, number>; colOrder: string[] } {
  try {
    const raw = localStorage.getItem(lsKey(gridId))
    if (raw) return JSON.parse(raw)
  } catch {}
  return { colWidths: {}, colOrder: [] }
}

function persistLayout(gridId: string, colWidths: Record<string, number>, colOrder: string[]) {
  try {
    localStorage.setItem(lsKey(gridId), JSON.stringify({ colWidths, colOrder }))
  } catch {}
}

export function useGridState(gridId: string): GridState {
  const initHash = readHashState()
  const initLayout = readLayout(gridId)

  const [sortRules, setSortRules] = useState<SortRule[]>(initHash.sort)
  const [freezeCount, setFreezeCountState] = useState(initHash.freeze)
  const [colWidths, setColWidthsState] = useState<Record<string, number>>(initLayout.colWidths)
  const [colOrder, setColOrderState] = useState<string[]>(initLayout.colOrder)

  useEffect(() => { writeHashState(sortRules, freezeCount) }, [sortRules, freezeCount])
  useEffect(() => { persistLayout(gridId, colWidths, colOrder) }, [gridId, colWidths, colOrder])

  const toggleSort = useCallback((col: string, multi: boolean) => {
    setSortRules(prev => {
      const existing = prev.find(r => r.col === col)
      if (multi) {
        if (!existing) return [...prev, { col, dir: 'asc' }]
        if (existing.dir === 'asc') return prev.map(r => r.col === col ? { ...r, dir: 'desc' } : r)
        return prev.filter(r => r.col !== col)
      }
      if (!existing) return [{ col, dir: 'asc' }]
      if (existing.dir === 'asc') return [{ col, dir: 'desc' }]
      return []
    })
  }, [])

  const setColWidth = useCallback((col: string, width: number) => {
    setColWidthsState(prev => ({ ...prev, [col]: width }))
  }, [])

  const setColOrder = useCallback((order: string[]) => {
    setColOrderState(order)
  }, [])

  const setFreezeCount = useCallback((n: number) => {
    setFreezeCountState(n)
  }, [])

  return { sortRules, freezeCount, colWidths, colOrder, toggleSort, setColWidth, setColOrder, setFreezeCount }
}
